/**
 * F36 (Wave 2E) — admin clubs moderation API client.
 *
 * Endpoints (per `plans/36_F36_trainer_managed_teams.md` §3.3 +
 * `apps/api/src/routes/admin/*clubs*`):
 *   GET    /admin/clubs                              → search (paginated, filters)
 *   GET    /admin/clubs/:b2bAccountId                → detail (roster + teams + audit)
 *   POST   /admin/clubs/:b2bAccountId/suspend        → suspendClub  (clubs:moderate)
 *   POST   /admin/clubs/:b2bAccountId/reinstate      → reinstateClub(clubs:moderate)
 *
 * F09 v2 retired `POST /admin/clubs/:id/verify` (plan §6): the club tick is an
 * F09 request decided under `/verifications`, and the club pages deep-link to
 * `/verifications/entities/club/:id`. Rows + the detail carry the tick as the
 * `verified` boolean (`ClubSummary.verified` / `ClubDetail.verified`).
 *
 * Permission gating happens server-side (super_admin + support +
 * content_moderator for read; super_admin + content_moderator for moderate —
 * the F36 §3.4 admin RBAC matrix). The page-level `<ProtectedRoute>` gates
 * which routes a given admin role can navigate to; this client forwards
 * requests and relies on the server to enforce RBAC.
 *
 * The vitest alias in `apps/admin/vitest.config.ts` rewrites every import of
 * `./api/clubs` (or `../../api/clubs`) to `./api/clubs.fixture` at test time so
 * component tests render against deterministic in-memory state.
 *
 * Response envelopes are defined LOCALLY here (the F29 `api/teams.ts`
 * precedent): the Wave-1 frozen `@bravemove/types` `club.ts` ships the consumer +
 * club-side shapes (`ClubSummary`, `ClubDetail`, `UpdateClubInput`,
 * `CLUB_ERROR_CODES`); the admin-moderation enrichment (display names,
 * verified state, roster + teams + audit detail) lives here, exactly the way
 * `AdminTeamRow` enriches `Team` in the F29 admin client.
 */
import type {
  B2bAccountMemberRole,
  B2bAuditEntry,
  ClubDetail,
  ClubSummary,
  Team,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

// ─── Suspend / reinstate input shapes ───────────────────────────────────────

/** POST /admin/clubs/:id/suspend — required free-form reason (1–500 chars). */
export interface SuspendClubInput {
  readonly reason: string;
}

/**
 * POST /admin/clubs/:id/reinstate — optional note saved on the audit trail.
 * Wire contract: the route's `.strict()` ReinstateBody accepts `{ note }` —
 * the previous `{ reason }` field 422'd whenever the admin filled the note in
 * (TCR-003). The value lands in the admin_audit_log payload via the F06
 * onSend hook.
 */
export interface ReinstateClubInput {
  readonly note?: string;
}

// ─── Response envelopes (admin-enriched) ─────────────────────────────────────

/**
 * A row in the admin clubs moderation queue — `ClubSummary` (which carries the
 * F09 v2 `verified` tick boolean) enriched with the owner display name so the
 * table doesn't have to round-trip per row. `verifiedAt` is display-only (the
 * tick renders from `verified`, never from the date — §13).
 */
export interface AdminClubRow extends ClubSummary {
  readonly verifiedAt: string | null;
  readonly ownerDisplayName: string | null;
}

export interface AdminClubsListResponse {
  readonly data: readonly AdminClubRow[];
  readonly nextCursor: string | null;
  /** Total count matching the filter. */
  readonly meta?: { readonly total?: number };
}

/** A roster row on the club detail page (staff member OR assigned trainer). */
export interface AdminClubRosterMember {
  readonly id: string;
  readonly userId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly role: B2bAccountMemberRole;
  readonly avatarUrl: string | null;
  readonly status: string;
  readonly joinedAt: string | null;
}

/** A club-managed team summary on the club detail "Teams" tab. */
export interface AdminClubTeam extends Team {
  readonly captainDisplayName: string | null;
  /** Count of active `team_trainers` rows (assigned club trainers). */
  readonly assignedTrainerCount: number;
}

export interface AdminClubDetailResponse {
  /** `club.verified` is the F09 v2 club tick (renders the tick). */
  readonly club: ClubDetail;
  /** Display-only "since" date for the hero tick. */
  readonly verifiedAt: string | null;
  readonly suspendedReason: string | null;
  readonly suspendedAt: string | null;
  readonly suspendedByAdminDisplayName: string | null;
  readonly members: readonly AdminClubRosterMember[];
  readonly teams: readonly AdminClubTeam[];
  readonly recentAuditEntries: readonly B2bAuditEntry[];
}

// ─── Query option types ──────────────────────────────────────────────────────

/** Server status filter — the club account lifecycle (active/suspended/disabled). */
export type AdminClubStatusFilter = ClubDetail['status'] | 'all';

export interface AdminClubsSearchOpts {
  readonly q?: string;
  readonly status?: AdminClubStatusFilter;
  readonly cursor?: string;
  readonly limit?: number;
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminClubsApi = {
  /** GET /admin/clubs — paginated search (filters: status / q). */
  search: (opts?: AdminClubsSearchOpts): Promise<AdminClubsListResponse> =>
    adminFetch<AdminClubsListResponse>(
      `/admin/clubs${buildQuery({
        q: opts?.q,
        status: opts?.status === 'all' ? undefined : opts?.status,
        cursor: opts?.cursor,
        limit: opts?.limit,
      })}`,
    ),

  /** GET /admin/clubs/:b2bAccountId — full detail (roster + teams + recent audit). */
  detail: (b2bAccountId: string): Promise<AdminClubDetailResponse | null> =>
    adminFetch<AdminClubDetailResponse>(`/admin/clubs/${encodeURIComponent(b2bAccountId)}`).catch(
      (err: unknown) => {
        if (
          err !== null &&
          typeof err === 'object' &&
          'status' in err &&
          (err as { status: number }).status === 404
        ) {
          return null;
        }
        throw err;
      },
    ),

  /** POST /admin/clubs/:b2bAccountId/suspend — D11 ops-retain cascade. */
  suspend: (b2bAccountId: string, body: SuspendClubInput): Promise<{ data: unknown }> =>
    adminFetch(`/admin/clubs/${encodeURIComponent(b2bAccountId)}/suspend`, {
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    }),

  /** POST /admin/clubs/:b2bAccountId/reinstate — re-show teams + resume schedules. */
  reinstate: (b2bAccountId: string, body: ReinstateClubInput = {}): Promise<{ data: unknown }> =>
    adminFetch(`/admin/clubs/${encodeURIComponent(b2bAccountId)}/reinstate`, {
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    }),
};
